import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiTrendingUp, FiTarget, FiClock, FiBookOpen, FiPlay } from 'react-icons/fi';
import { insightsAPI, api } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TEMPLATE_GAMES = [
  { id: 'QUIZ_RUSH', name: 'Quiz Rush', description: 'Fast-paced quiz with time pressure', icon: '🧠' },
  { id: 'MEMORY_MATCH', name: 'Memory Match', description: 'Match concepts with definitions', icon: '🔗' },
  { id: 'WORD_BUILDER', name: 'Word Builder', description: 'Build terms from letters', icon: '🔤' },
  { id: 'FORMULA_RACE', name: 'Formula Race', description: 'Complete formulas quickly', icon: '🔢' },
  { id: 'CHAIN_REACTION', name: 'Chain Reaction', description: 'Answer in sequence for multipliers', icon: '⚡' }
];

const Insights = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metrics, setMetrics] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await insightsAPI.getGuardianInsights();
      const data = resp?.data?.data || resp?.data || {};
      setMetrics(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError('Please sign in as a parent to view Insights.');
      else if (status === 403) setError('Your account is not authorized to view Insights.');
      else setError('Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-sm text-slate-500">Loading insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900">Analytics insights</h1>
          <p className="text-sm text-slate-500 mt-0.5">Insights for your associated students</p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-800">{error}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><FiTarget size={20} /></div>
              <div><div className="text-2xl font-semibold text-slate-900">{Math.round(Number(metrics.averageScore || 0))}%</div><div className="text-xs text-slate-500">Average score</div></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiBookOpen size={20} /></div>
              <div><div className="text-2xl font-semibold text-slate-900">{metrics.totalTests ?? 0}</div><div className="text-xs text-slate-500">Tests</div></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><FiClock size={20} /></div>
              <div><div className="text-2xl font-semibold text-slate-900">{Math.round((Number(metrics.timeSpentTotal || 0)) / 3600)}h</div><div className="text-xs text-slate-500">Study hours</div></div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center text-violet-600"><FiTrendingUp size={20} /></div>
              <div><div className="text-2xl font-semibold text-slate-900">{Math.round(Number(metrics.scoreImprovement || 0))}%</div><div className="text-xs text-slate-500">Improvement</div></div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center justify-between border-b border-slate-200 pb-3 mb-4">
            <h2 className="text-sm font-medium text-slate-900">Learning games</h2>
            <Link to="/games" className="text-sm font-medium text-slate-600 hover:text-slate-900">View all games</Link>
          </div>
          <p className="text-sm text-slate-500 mb-4">Encourage your child to practice with these games. Each game reinforces subjects and exam-style thinking.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {TEMPLATE_GAMES.map((game) => (
              <Link
                key={game.id}
                to={`/games/play/${game.id}`}
                className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 hover:border-slate-300 hover:shadow-md transition-all flex flex-col"
              >
                <div className="text-2xl mb-2" aria-hidden>{game.icon}</div>
                <h3 className="text-sm font-medium text-slate-900 mb-1">{game.name}</h3>
                <p className="text-xs text-slate-500 flex-1">{game.description}</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-slate-700">
                  <FiPlay size={14} /> Play
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights;
